import React from 'react'
import { View, Text, ActivityIndicator } from 'react-native'
import { GAME_TOP_BAR_HEIGHT } from '../../lib/gameChrome'

export type GameBannerTone = 'info' | 'warning' | 'error'

export interface GameBannerProps {
  /** The notice text. Also used as the accessibility label. */
  message: string
  /** Visual severity. Defaults to 'info' (neutral). */
  tone?: GameBannerTone
  /** Show an inline spinner — for in-progress states (e.g. reconnecting). */
  spinner?: boolean
  /** Top safe-area inset so the bar clears the notch / status bar. */
  topInset: number
  /** Place a notice in its owner's layout rather than over the screen. */
  inline?: boolean
}

const TONE: Record<
  GameBannerTone,
  { container: string; text: string; spinner: string }
> = {
  // Neutral — connection status and other non-alarming notices.
  info: { container: 'bg-paper-up border-olive', text: 'text-ink-soft', spinner: '#94a3b8' },
  // Amber — something the player should notice but isn't broken.
  warning: { container: 'bg-amber-100 border-amber-700', text: 'text-amber-800', spinner: '#b45309' },
  // Blood — an actual error during play.
  error: { container: 'bg-blood border-blood', text: 'text-white', spinner: '#ffffff' },
}

/**
 * GameBanner — the in-game notice bar.
 *
 * A full-height bar that overlays the in-game top bar
 * without pushing content down, and with `pointerEvents="none"` so it never
 * steals a tap meant for the cog / board beneath it.
 *
 * This is the shared vehicle for transient in-game status, warnings, and
 * errors (connection reconnecting, and any other issue surfaced mid-game).
 * It is distinct from the bottom Toast (components/ui/Toast.tsx), which is for
 * brief, auto-dismissing action feedback. Reach for GameBanner when the notice
 * reflects an ongoing condition the player should keep seeing until it clears.
 */
export function GameBanner({ message, tone = 'info', spinner = false, topInset, inline = false }: GameBannerProps) {
  const t = TONE[tone]
  return (
    <View
      pointerEvents="none"
      accessibilityLabel={message}
      className={`${inline ? '' : 'absolute left-0 right-0'} flex-row items-center justify-center gap-2 px-4 border-b ${t.container}`}
      style={inline ? { minHeight: 24, paddingVertical: 4 } : { top: topInset, height: GAME_TOP_BAR_HEIGHT, zIndex: 50, elevation: 50 }}
    >
      {spinner && <ActivityIndicator size="small" color={t.spinner} />}
      <Text className={`font-typed text-xs ${t.text}`} style={{ flexShrink: 1 }}>{message}</Text>
    </View>
  )
}
